import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Copy, ExternalLink, Pencil, Trash2, Users } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { copyText, imageUrl, shareUrl } from "../lib/api";
import { formatShortDate, formatTime } from "../lib/format";

const iconBtn =
  "inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-[background-color,color] duration-200 hover:bg-slate-900 hover:text-white";

const Chip = ({ label, value, cls, testid }) => (
  <span data-testid={testid} className={`rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>
    {value} {label}
  </span>
);

export const EventCard = ({ event, onDelete }) => {
  const s = event.stats || {};
  const copy = async () => {
    await copyText(shareUrl(event.slug));
    toast.success("Invite link copied");
  };
  return (
    <article data-testid={`event-card-${event.id}`} className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl">
      <Link to={`/events/${event.id}`} className="relative block aspect-[16/10] overflow-hidden bg-slate-50" data-testid={`event-card-image-link-${event.id}`}>
        {event.image_path ? (
          <img src={imageUrl(event.image_path, event.access_token)} alt={event.occasion} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="confetti-bg-white h-full w-full" />
        )}
        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-800 backdrop-blur">
          <CalendarDays className="h-3.5 w-3.5 text-blue-700" />
          {formatShortDate(event.date)}{event.time ? ` · ${formatTime(event.time)}` : ""}
        </span>
      </Link>
      <div className="flex flex-1 flex-col gap-4 p-6">
        <div>
          <h3 data-testid={`event-card-title-${event.id}`} className="font-display text-xl font-bold tracking-tight text-slate-900">{event.occasion}</h3>
          {event.place && <p className="mt-1 truncate text-sm text-slate-500">{event.place}</p>}
          {event.role === "cohost" && (
            <p data-testid={`event-card-cohost-${event.id}`} className="mt-2 flex items-center gap-2 text-xs text-slate-500">
              <span className="rounded-full bg-blue-50 px-2.5 py-1 font-bold uppercase tracking-[0.1em] text-blue-700">Co-host</span>
              <span className="truncate">Owner: {event.owner_name}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testid={`event-card-yes-${event.id}`} label="yes" value={s.yes || 0} cls="bg-emerald-50 text-emerald-700" />
          <Chip testid={`event-card-maybe-${event.id}`} label="maybe" value={s.maybe || 0} cls="bg-amber-50 text-amber-700" />
          <Chip testid={`event-card-no-${event.id}`} label="no" value={s.no || 0} cls="bg-red-50 text-red-700" />
          <span data-testid={`event-card-headcount-${event.id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-xs font-bold text-white">
            <Users className="h-3 w-3" /> {s.headcount || 0} attending
          </span>
        </div>
        <div className="mt-auto flex items-center gap-2 pt-2">
          <Link to={`/events/${event.id}`} data-testid={`event-responses-btn-${event.id}`} className="mr-auto rounded-lg bg-blue-700 px-4 py-2 text-sm font-bold text-white transition-[background-color] duration-200 hover:bg-blue-800 hover:text-white">
            Responses
          </Link>
          <button title="Copy link" data-testid={`event-copy-link-btn-${event.id}`} onClick={copy} className={iconBtn}><Copy className="h-4 w-4" /></button>
          <a title="Open invite" data-testid={`event-open-btn-${event.id}`} href={`/e/${event.slug}`} target="_blank" rel="noopener noreferrer" className={iconBtn}><ExternalLink className="h-4 w-4" /></a>
          <Link title="Edit" data-testid={`event-edit-btn-${event.id}`} to={`/events/${event.id}/edit`} className={iconBtn}><Pencil className="h-4 w-4" /></Link>
          {event.role !== "cohost" && <AlertDialog>
            <AlertDialogTrigger asChild>
              <button title="Delete" data-testid={`event-delete-btn-${event.id}`} className={`${iconBtn} hover:bg-red-600`}><Trash2 className="h-4 w-4" /></button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{event.occasion}"?</AlertDialogTitle>
                <AlertDialogDescription>The invite link will stop working and all RSVPs will be removed.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="event-delete-cancel-btn" className="rounded-lg">Keep it</AlertDialogCancel>
                <AlertDialogAction data-testid="event-delete-confirm-btn" className="rounded-lg bg-red-600 hover:bg-red-700" onClick={() => onDelete(event)}>
                  Delete event
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
        </div>
      </div>
    </article>
  );
};
